import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ScanEye, CheckCircle2, XCircle, Loader2, Copy, Check, Download, AlertTriangle, Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import usePageMeta from '../hooks/usePageMeta';
import { commandFor, fmtDateTime } from '../components/candor/helpers';

// Public page a candidate opens from the interviewer's link. No login: the
// token in the path is the credential. It explains what the check looks at and
// sends, asks for agreement, then shows the one command to run. One screen, two
// columns: the action on the left, the quieter "what this does" reference on the
// right. Content fades/staggers in so it doesn't all land at once. Toasts use
// the app-wide Toaster (App.js) - no local one, or they show twice.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 4000;
// AuthContext turns on withCredentials for every axios call. This page needs
// no cookies, and a cookie-carrying call is blocked by CORS whenever the page
// is opened on a host other than the API's (e.g. the *.emergent.host name).
const NO_COOKIES = { withCredentials: false };

// Short labels only - the detail lived in long descriptions that bloated the
// page; the label says enough and the privacy reassurance is below.
const CHECKS = [
  'Windows hidden from screen sharing',
  'Always-on-top overlay windows',
  'Known interview-assist apps',
  'Remote-control software',
  'AI apps, sites and APIs contacted',
  'Extra displays, virtual machine, virtual audio / camera',
  'Which app you have in focus',
];

const NOT_DONE = [
  'No screenshots, recording, camera, mic or keystrokes.',
  "Doesn't open or upload your files, messages or history.",
  'Installs nothing, changes nothing, needs no admin rights.',
  'Beyond your focused window, only matches are sent.',
];

// Entrance + micro-interaction animations, all disabled under reduced-motion.
const ANIM_CSS = `
@keyframes candorIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes candorPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: .4; transform: scale(.85); } }
@keyframes candorPop { 0% { transform: scale(.6); opacity: 0; } 60% { transform: scale(1.12); } 100% { transform: scale(1); opacity: 1; } }
.candor-in { opacity: 0; animation: candorIn .5s cubic-bezier(.16,.84,.44,1) forwards; }
.candor-dot { animation: candorPulse 1.6s ease-in-out infinite; }
.candor-pop { animation: candorPop .4s cubic-bezier(.16,.84,.44,1) both; }
@media (prefers-reduced-motion: reduce) {
  .candor-in, .candor-dot, .candor-pop { animation: none !important; opacity: 1 !important; transform: none !important; }
}
`;

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex flex-col">
    <style>{ANIM_CSS}</style>
    <header className="bg-white border-b shrink-0">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 h-12 flex items-center gap-2">
        <ScanEye className="w-5 h-5 text-indigo-600" />
        <span className="font-semibold">Harmony Candor</span>
        <span className="text-sm text-muted-foreground">· interview integrity check</span>
      </div>
    </header>
    <main className="max-w-5xl w-full mx-auto px-4 sm:px-6 py-10 flex-1">{children}</main>
  </div>
);

const Notice = ({ icon: Icon, tone, title, children }) => {
  const tones = { ok: 'text-emerald-600', bad: 'text-red-600', muted: 'text-slate-400' };
  return (
    <div className="bg-white border rounded-2xl p-8 text-center max-w-md mx-auto mt-10 candor-in">
      <Icon className={`w-10 h-10 mx-auto mb-3 ${tones[tone]}`} />
      <h1 className="text-lg font-semibold">{title}</h1>
      <div className="text-sm text-muted-foreground mt-2 space-y-2">{children}</div>
    </div>
  );
};

const CommandBlock = ({ scriptUrl }) => {
  const [copied, setCopied] = useState(false);
  const cmd = commandFor(scriptUrl);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(cmd);
      toast.success('Command copied');
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error('Could not copy — select the text and copy it manually');
    }
  };
  return (
    <div className="space-y-2">
      <pre className="bg-slate-900 text-slate-100 text-xs rounded-lg p-3 whitespace-pre-wrap break-all">{cmd}</pre>
      <Button size="sm" onClick={onCopy} className="transition-colors" data-testid="candor-copy-command">
        {copied
          ? <><Check className="w-4 h-4 mr-1.5 candor-pop" />Copied</>
          : <><Copy className="w-4 h-4 mr-1.5" />Copy command</>}
      </Button>
    </div>
  );
};

const DownloadNote = ({ scriptUrl }) => (
  <p className="text-xs text-muted-foreground">
    Prefer to read it first?{' '}
    <a href={`${scriptUrl}?download=true`} className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
      <Download className="w-3.5 h-3.5" />Download the script
    </a>{' '}
    — but run the command above; some antivirus tools delete a downloaded copy.
  </p>
);

// Quieter right-hand reference column. Headings recede; the checks cascade in.
const AboutCard = () => (
  <aside className="rounded-2xl border bg-white/70 p-6 space-y-6 text-sm candor-in" style={{ animationDelay: '120ms' }}>
    <div>
      <h2 className="text-xs font-medium uppercase tracking-wide text-slate-400 flex items-center gap-1.5 mb-3">
        <Eye className="w-3.5 h-3.5" />What it looks for
      </h2>
      <ul className="space-y-2 text-slate-600">
        {CHECKS.map((c, i) => (
          <li key={c} className="flex gap-2 candor-in" style={{ animationDelay: `${200 + i * 60}ms` }}>
            <span className="text-indigo-400 mt-px">•</span>{c}
          </li>
        ))}
      </ul>
    </div>
    <div className="border-t pt-5">
      <h2 className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-3">What it never does</h2>
      <ul className="space-y-2 text-slate-600">
        {NOT_DONE.map((c) => (
          <li key={c} className="flex gap-2"><Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />{c}</li>
        ))}
      </ul>
    </div>
    <div className="border-t pt-5">
      <h2 className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-2">What your interviewer sees</h2>
      <p className="text-slate-500 leading-relaxed">
        Your PC name, Windows user and version, IP address, each match found, the app you have in focus, and whether the
        check is still running.
      </p>
    </div>
  </aside>
);

const CandorCandidatePage = () => {
  usePageMeta('Interview check | Harmony Candor', '/favicon-candor.svg');
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  const base = `${BACKEND_URL}/api/candor/public/s/${encodeURIComponent(token || '')}`;
  const scriptUrl = `${BACKEND_URL || window.location.origin}/api/candor/public/s/${encodeURIComponent(token || '')}/script`;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(base, NO_COOKIES);
      setInfo(r.data);
      setError(null);
      if (r.data?.consented) setConfirmed(true);
    } catch (e) {
      setError(e?.response?.status === 404 ? 'invalid' : 'network');
    }
  }, [base]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const agree = async () => {
    setSaving(true);
    try {
      await axios.post(`${base}/consent`, null, NO_COOKIES);
      setConfirmed(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reach the server — try again');
    } finally {
      setSaving(false);
    }
  };

  if (!info && !error) {
    return <Shell><div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div></Shell>;
  }
  if (error === 'invalid') {
    return (
      <Shell>
        <Notice icon={XCircle} tone="bad" title="This link isn't valid">
          <p>Check you copied the whole link, or ask your interviewer to send it again.</p>
        </Notice>
      </Shell>
    );
  }
  if (!info) {
    return (
      <Shell>
        <Notice icon={AlertTriangle} tone="muted" title="Can't reach the server">
          <p>Check your internet connection. This page will retry by itself.</p>
        </Notice>
      </Shell>
    );
  }
  if (info.state === 'ended') {
    return (
      <Shell>
        <Notice icon={CheckCircle2} tone="ok" title="This interview check has ended">
          <p>Thanks{info.candidate_name ? `, ${info.candidate_name}` : ''}. If the PowerShell window is still open you can close it.</p>
        </Notice>
      </Shell>
    );
  }
  if (info.state === 'expired') {
    return (
      <Shell>
        <Notice icon={XCircle} tone="muted" title="This link has expired">
          <p>Ask your interviewer for a new one.</p>
        </Notice>
      </Shell>
    );
  }

  const connected = info.state === 'live';
  const lost = info.state === 'silent';

  return (
    <Shell>
      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8 lg:gap-12 items-start">
        {/* Left: the action */}
        <div className="space-y-6">
          <div className="candor-in">
            <p className="text-sm text-muted-foreground">Hi {info.candidate_name},</p>
            <h1 className="text-xl font-semibold leading-snug mt-1">
              {info.interviewer || 'Your interviewer'} has asked you to run a quick integrity check during your interview
              {info.role_title ? ` for ${info.role_title}` : ''}.
            </h1>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              A small PowerShell script runs on your Windows PC during the call and flags AI interview-assist tools.
              Nothing is installed; it stops when the interview ends or you close its window.
              {info.scheduled_at ? ` Interview: ${fmtDateTime(info.scheduled_at)}.` : ''}
            </p>
          </div>

          {connected && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3 candor-in" data-testid="candor-connected">
              <span className="relative flex w-2.5 h-2.5 mt-1.5 shrink-0">
                <span className="candor-dot absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-emerald-500" />
              </span>
              <div>
                <p className="font-medium text-emerald-900">Connected{info.connected_computer ? ` from ${info.connected_computer}` : ''}</p>
                <p className="text-sm text-emerald-800/80">Keep the PowerShell window open (minimising is fine) until your interview ends.</p>
              </div>
            </div>
          )}

          {lost && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3 candor-in">
              <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
              <div>
                <p className="font-medium text-amber-900">The check stopped reporting</p>
                <p className="text-sm text-amber-800/80">If you closed the window or your PC slept, run the command again.</p>
              </div>
            </div>
          )}

          {!confirmed && (
            <div className="bg-white border rounded-2xl p-6 space-y-3 candor-in" style={{ animationDelay: '80ms' }}>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" data-testid="candor-agree" />
                <span className="text-sm">I've read what this check does (on the right) and agree to run it on my PC for this interview.</span>
              </label>
              <p className="text-xs text-muted-foreground">If you'd rather not, or you aren't on Windows, tell your interviewer before the call.</p>
              <Button onClick={agree} disabled={!agreed || saving} data-testid="candor-agree-continue">
                {saving ? 'Saving…' : 'Continue'}
              </Button>
            </div>
          )}

          {confirmed && !connected && (
            <div className="bg-white border rounded-2xl p-6 space-y-4 candor-in" style={{ animationDelay: '80ms' }} data-testid="candor-steps">
              <div className="flex items-center justify-between">
                <h2 className="font-medium">Run the check</h2>
                {!lost && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="candor-dot w-2 h-2 rounded-full bg-indigo-500" />Waiting for it to connect…
                  </span>
                )}
              </div>
              <ol className="space-y-4 text-sm">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center shrink-0">1</span>
                  <span className="pt-0.5">Press <kbd className="px-1.5 py-0.5 border rounded bg-slate-50 text-xs">Win</kbd> + <kbd className="px-1.5 py-0.5 border rounded bg-slate-50 text-xs">R</kbd>, type <code className="bg-slate-100 px-1 rounded">powershell</code> and press Enter.</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center shrink-0">2</span>
                  <div className="min-w-0 flex-1 space-y-2">
                    <span className="block pt-0.5">Paste this command (right-click pastes), press Enter, then wait for <span className="font-medium">Connected</span>.</span>
                    <CommandBlock scriptUrl={scriptUrl} />
                  </div>
                </li>
              </ol>
              <div className="border-t pt-4"><DownloadNote scriptUrl={scriptUrl} /></div>
            </div>
          )}

          {confirmed && connected && (
            <details className="bg-white border rounded-2xl p-4 candor-in" style={{ animationDelay: '80ms' }}>
              <summary className="text-sm font-medium cursor-pointer select-none">Need to run it again?</summary>
              <div className="mt-3 space-y-3">
                <p className="text-sm text-slate-600">Open PowerShell (<kbd className="px-1.5 py-0.5 border rounded bg-slate-50 text-xs">Win</kbd> + <kbd className="px-1.5 py-0.5 border rounded bg-slate-50 text-xs">R</kbd> → <code className="bg-slate-100 px-1 rounded">powershell</code>), then paste and run:</p>
                <CommandBlock scriptUrl={scriptUrl} />
                <DownloadNote scriptUrl={scriptUrl} />
              </div>
            </details>
          )}
        </div>

        {/* Right: the quiet reference / disclosure */}
        <AboutCard />
      </div>
    </Shell>
  );
};

export default CandorCandidatePage;
